import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, Link } from '@mui/icons-material';
import { Divider, SxProps, Theme } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import { v4 as uuidv4 } from 'uuid';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { IOType } from 'features/systems/domain/models/io-type';
import CreateDomipDeviceCommand from 'features/systems/domip/domain/models/create-domip-device-command';
import DomipProviderContext from 'features/systems/domip/providers/contexts/domip-provider-context';
import styles from './domip-inputs-component.module.scss';

const AmountOfDomipInputs = 11;

const DomipInputs = (): React.JSX.Element => {
  const { t } = useTranslation('domip');
  const { t: tIOEventTypes } = useTranslation('ioEventTypes');

  const { hasPermission } = usePermissionAuthorization();

  const {
    createDomipDevice,
    domipPulseNumberHasDevice,
    domipPulseNumberHasLinkedDevice,
    openDeviceDetails,
    getBreadCrumbsForDomipPulseNumber,
    getDomipInputDevicePulseNumber,
  } = use(DomipProviderContext);

  const {
    systemsDetailsHook: { viewingMode, selectedSystem },
  } = useSystemsContextProvider();

  const textSx: SxProps<Theme> = {
    '&.Mui-disabled': {
      color: Constants.Colors.primary,
      opacity: 0.38,
    },
  };

  return (
    <>
      <div className={styles.inputs}>
        <Typography variant="h5">{t('inputs.title')}</Typography>
        {Array.from({ length: AmountOfDomipInputs }, (_, index) => {
          const domipPulseNumber = index + 1;
          const domipInputDevice = getDomipInputDevicePulseNumber(domipPulseNumber);
          return (
            <div key={`input-${uuidv4()}`} className={styles.input}>
              <div className={styles.titleContainer}>
                <Typography
                  variant="subtitle1"
                  className={`${styles.inputLabel} ${viewingMode === 'viewing' && domipPulseNumberHasDevice(domipPulseNumber, false) ? styles.clickable : ''}`}
                  onClick={() =>
                    viewingMode === 'viewing' &&
                    domipPulseNumberHasDevice(domipPulseNumber, false) &&
                    openDeviceDetails(domipPulseNumber, false)
                  }
                >
                  {t(`inputs.input`, { number: index + 1 })}
                </Typography>

                {domipPulseNumberHasLinkedDevice(domipPulseNumber, false) && (
                  <div className={styles.linkButton}>
                    <BreadCrumbTooltip allElementNames={getBreadCrumbsForDomipPulseNumber(domipPulseNumber, false)}>
                      <Link sx={{ fontSize: 18 }} />
                    </BreadCrumbTooltip>
                  </div>
                )}
              </div>

              <div className={styles.functionalNameContainer}>
                <Typography variant="subtitle1">{domipInputDevice?.functionalName}</Typography>
              </div>

              <div className={styles.eventsContainer}>
                <Typography variant="subtitle1">{`${t('inputs.eventTypeLow')}: ${domipInputDevice?.domipEvent?.eventTypeLow ? tIOEventTypes(domipInputDevice?.domipEvent?.eventTypeLow) : '-'}`}</Typography>
                <Typography variant="subtitle1">{`${t('inputs.eventTypeHigh')}: ${domipInputDevice?.domipEvent?.eventTypeHigh ? tIOEventTypes(domipInputDevice?.domipEvent?.eventTypeHigh) : '-'}`}</Typography>
              </div>

              <div className={styles.createDeviceContainer}>
                {!domipPulseNumberHasDevice(domipPulseNumber, false) && hasPermission(Permission.CreateDevice) && (
                  <Button
                    startIcon={<AddCircleOutlineRounded />}
                    disabled={viewingMode === 'editing'}
                    sx={textSx}
                    onClick={() => {
                      if (selectedSystem?.id) {
                        const command = {
                          systemId: selectedSystem.id,
                          domipPulseNumber: index + 1,
                          ioType: IOType.input,
                        } as CreateDomipDeviceCommand;
                        createDomipDevice(command);
                      }
                    }}
                  >
                    {t('inputs.createDevice')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Divider sx={{ width: '100%' }} />
    </>
  );
};

export default DomipInputs;
